/**
 * Wallet top-up credited. A positive transactional notice, not an alert: it
 * confirms the credit the tenant just initiated, so emitters do not write an
 * operator `billing_notifications` row for it.
 */

import type { TenantEventDefinition } from './definition'

export const BILLING_TOPUP_CREDITED_EVENT_TYPE = 'billing_topup_credited' as const

export interface BillingTopupCreditedPayload {
  /** What the tenant pays for the top-up, in cents. */
  priceCents: number
  /** What the wallet was credited (price + plan bonus), in microUSD. */
  creditMicrousd: number
  bonusPercent: number
  /** Wallet balance after the credit, in microUSD. */
  balanceMicrousd: number
  /** True when the charge lands on the store's next Shopify invoice. */
  viaShopifyInvoice: boolean
  /** When the credited top-up expires; null or absent = never. */
  expiresAt?: string | null
}

function isFiniteInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

function isTopupCreditedPayload(payload: unknown): payload is BillingTopupCreditedPayload {
  if (typeof payload !== 'object' || payload === null) return false
  const p = payload as Record<string, unknown>
  return (
    isFiniteInteger(p.priceCents) &&
    isFiniteInteger(p.creditMicrousd) &&
    isFiniteInteger(p.bonusPercent) &&
    isFiniteInteger(p.balanceMicrousd) &&
    typeof p.viaShopifyInvoice === 'boolean' &&
    (p.expiresAt === undefined || p.expiresAt === null || typeof p.expiresAt === 'string')
  )
}

export const billingTopupCreditedEventDefinition: TenantEventDefinition<BillingTopupCreditedPayload> =
  {
    eventType: BILLING_TOPUP_CREDITED_EVENT_TYPE,
    supportedChannelTypes: ['email', 'telegram'],
    validatePayload: isTopupCreditedPayload,
    tenant: {
      label: 'Top-up received',
      description: 'A top-up reached the balance: the receipt with the credits added and the new balance.',
      recipients: 'account_alerts',
      mandatory: false,
    },
  }
